import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { withArtikelImage } from "@/components/ArtikelImage";
import { Autocomplete, type AutocompleteOption } from "@/components/Autocomplete";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import { TextInput } from "@/components/ui/Field";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { useCurrentUser } from "@/lib/auth";
import { heroArticleOrderText, searchHeroArticles, type HeroArticleSuggestion } from "@/lib/material";
import { COMMON_MATERIAL_UNITS, resolveMaterialUnit } from "@/lib/materialUnits";
import { NativeSelectField } from "@/components/ui/NativeSelect";
import {
  listDeliveryAddresses,
  listEmailTemplates,
  peekOrderNumber,
  getTemplatePreference,
  saveTemplatePreference,
  topOrderProducts,
  type DeliveryAddress,
  type EmailTemplate,
  type HeroSupplier,
  type HeroSupplierContact,
} from "@/lib/orders";
import {
  composeLieferadresseString,
  computeTkPreview,
  formatAuftragHeadline,
  newPositionId,
  orderTextWithArticleNumber,
  persistLocalDraft,
  addressFieldsFromDelivery,
  clearAddressFields,
  getDefaultOrderZhd,
  type DraftPosition,
  type OrderLieferart,
  type OrderSupplierState,
  type OrderWizardState,
} from "@/lib/orderDraftUtils";
import type { AuftragMini } from "@/lib/orders";
import {
  contactToAutocompleteOption,
  defaultContactWhenNoPersons,
  loadOrderSupplierContactOptions,
  loadSupplierContactChoices,
  loadOrderSupplierOptions,
  supplierFieldsFromContact,
  supplierLogoState,
  supplierToAutocompleteOption,
} from "@/lib/orderSupplierAutocomplete";
import { DeliveryAddressDialog } from "@/pages/bestellungen/DeliveryAddressDialog";
import { OrderDraftPickerPanel } from "@/pages/bestellungen/OrderDraftPickerPanel";
import { MaWizardSplitLayout } from "@/pages/material/MaContextPanel";
import type { OrderDraft } from "@/lib/orders";

const STEPS = [
  { key: "supplier", label: "Lieferant" },
  { key: "auftrag", label: "Auftrag" },
  { key: "positions", label: "Artikel" },
] as const;

function isSupplierReady(s: OrderWizardState): boolean {
  if (!s.supplier) return false;
  if (s.supplier.id != null && String(s.supplier.id).trim() !== "") return true;
  return !!(s.supplier.name || "").trim();
}

/** Snapshot ohne volatile Felder (tkPreview wird nach dem Öffnen asynchron gesetzt). */
function wizardDirtySnapshot(s: OrderWizardState): string {
  const { tkPreview: _tkPreview, ...rest } = s;
  return JSON.stringify(rest);
}

export function OrderWizardDialog({
  open,
  state,
  setState,
  title,
  drafts,
  showDraftPicker,
  onLoadDraft,
  onDeleteDraft,
  onClose,
  onSaveDraft,
  onOrder,
  onPrint,
  showPrintButton,
  onUpdate,
  onUpdateAndMail,
}: {
  open: boolean;
  state: OrderWizardState;
  setState: React.Dispatch<React.SetStateAction<OrderWizardState>>;
  title: string;
  drafts: OrderDraft[];
  showDraftPicker: boolean;
  onLoadDraft: (draft: OrderDraft) => void;
  onDeleteDraft: (id: number) => void;
  onClose: () => void;
  onSaveDraft: () => void;
  onOrder: () => void;
  onPrint?: () => void;
  showPrintButton?: boolean;
  onUpdate: () => void;
  onUpdateAndMail: () => void;
}) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [addresses, setAddresses] = useState<DeliveryAddress[]>([]);
  const [addrDialog, setAddrDialog] = useState<"new" | "edit" | null>(null);
  const deliveryInitRef = useRef(false);
  const dirtyBaselineRef = useRef<string | null>(null);
  const supplierPickSeq = useRef(0);
  const { user } = useCurrentUser();

  const editing = Number(state.editingOrderId) > 0;
  const isLieferung = state.lieferart !== "abholung";
  const defaultZhd = useMemo(
    () => getDefaultOrderZhd(user, state.kuerzel),
    [user, state.kuerzel],
  );
  const hasSupplier = isSupplierReady(state);
  const hasAuftrag = state.auftraege.length > 0;
  const hasPositions = state.positionen.length > 0;

  const activeStep = !hasSupplier ? "supplier" : !hasAuftrag ? "auftrag" : "positions";

  const supplierValue = useMemo(
    () => supplierToAutocompleteOption(state.supplier),
    [state.supplier],
  );
  const contactValue = useMemo(
    () => contactToAutocompleteOption(state.supplier),
    [state.supplier],
  );

  const reloadAddresses = useCallback(async () => {
    const list = await listDeliveryAddresses().catch(() => []);
    const normalized = Array.isArray(list) ? list : [];
    setAddresses(normalized);
    return normalized;
  }, []);

  useEffect(() => {
    if (!open) return;
    void listEmailTemplates().then((t) => setTemplates(Array.isArray(t) ? t : []));
    void reloadAddresses();
  }, [open, reloadAddresses]);

  useEffect(() => {
    if (!open || !state.auftraege.length || !state.kuerzel) return;
    void peekOrderNumber(new Date().getFullYear()).then((data) => {
      const last = Number(data.last_lauf ?? 0) || 0;
      setState((s) => ({ ...s, tkPreview: computeTkPreview(s.auftraege, s.kuerzel, last) }));
    });
  }, [open, state.auftraege, state.kuerzel, setState]);

  useEffect(() => {
    if (open) persistLocalDraft(state);
  }, [open, state]);

  useEffect(() => {
    if (open) {
      dirtyBaselineRef.current = wizardDirtySnapshot(state);
    } else {
      dirtyBaselineRef.current = null;
    }
    // Snapshot nur beim Öffnen erfassen – Änderungen danach zählen als "dirty".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Bearbeiten: Diff gegen Stand beim Öffnen. Neuanlage: sobald relevante
  // Inhalte vorhanden sind (Adress-Vorbelegung nach dem Öffnen zählt nicht).
  const hasNewContent =
    isSupplierReady(state) ||
    state.auftraege.length > 0 ||
    state.positionen.some((p) => (p.text || "").trim() !== "");
  const editingChanged =
    dirtyBaselineRef.current != null &&
    wizardDirtySnapshot(state) !== dirtyBaselineRef.current;
  const wizardDirty = Number(state.editingOrderId) > 0 ? editingChanged : hasNewContent;

  function setSupplier(patch: Partial<OrderSupplierState> | null) {
    setState((s) => ({
      ...s,
      supplier: patch === null ? null : { ...(s.supplier || { id: null, name: "" }), ...patch },
    }));
  }

  async function selectSupplier(opt: { value: HeroSupplier } | null) {
    if (!opt) {
      supplierPickSeq.current += 1;
      setSupplier(null);
      return;
    }
    const sameId =
      state.supplier?.id != null && String(state.supplier.id) === String(opt.value.id);
    const companyEmail = (opt.value.email || "").trim();
    const logo = supplierLogoState(opt.value);
    if (sameId) {
      setSupplier({
        id: opt.value.id,
        name: opt.value.name || "",
        company_email: companyEmail || state.supplier?.company_email || "",
        ...logo,
      });
      return;
    }
    const seq = ++supplierPickSeq.current;
    const next: OrderSupplierState = {
      id: opt.value.id,
      name: opt.value.name || "",
      company_email: companyEmail,
      anrede: "",
      email: "",
      contact_first_name: "",
      contact_last_name: "",
      person: "",
      ...logo,
    };
    setSupplier(next);
    try {
      const loaded = await loadSupplierContactChoices(next, "");
      if (supplierPickSeq.current !== seq || !loaded.contactsLoaded) return;
      const general = defaultContactWhenNoPersons(loaded.options);
      if (!general) return;
      const fields = supplierFieldsFromContact(general);
      let didApply = false;
      setState((s) => {
        if (supplierPickSeq.current !== seq) return s;
        const cur = s.supplier;
        if (!cur || String(cur.id) !== String(next.id)) return s;
        const alreadyChosen = [cur.email, cur.contact_first_name, cur.contact_last_name, cur.person].some(
          (part) => (part || "").trim() !== "",
        );
        if (alreadyChosen) return s;
        didApply = true;
        return { ...s, supplier: { ...cur, ...fields } };
      });
      if (!didApply) return;
      const contactKey = (fields.email || "").trim().toLowerCase();
      void applyTemplatePreferenceForContact({ ...next, ...fields }, contactKey);
    } catch {
      // Ohne geladene Ansprechpartner bleibt die Auswahl leer.
    }
  }

  async function applyTemplatePreferenceForContact(supplier: OrderSupplierState, contactKey: string) {
    const supplierId = supplier.id != null ? String(supplier.id) : "";
    if (!supplierId || supplierId.startsWith("name:") || !contactKey) return;
    let templateId: number | null = null;
    try {
      const pref = await getTemplatePreference(supplierId, contactKey);
      if (pref?.template_id) templateId = Number(pref.template_id);
    } catch {
      /* ignore */
    }
    if (!templateId) {
      const def = templates.find((t) => t.is_default);
      if (def) templateId = def.id;
    }
    if (templateId) {
      setState((s) => ({ ...s, templateId }));
    }
  }

  function addAuftrag(a: AuftragMini) {
    setState((s) => {
      if (s.auftraege.some((x) => x.id === a.id)) return s;
      const placeholders = Math.max(0, s.placeholders - 1);
      return { ...s, auftraege: [...s.auftraege, a], placeholders };
    });
  }

  function removeAuftrag(id: number) {
    setState((s) => ({
      ...s,
      auftraege: s.auftraege.filter((a) => a.id !== id),
      positionen: s.positionen.filter((p) => p.auftrag_id !== id),
      placeholders: s.auftraege.length <= 1 ? Math.max(1, s.placeholders) : s.placeholders,
    }));
  }

  function addPosition(aid: number, partial: Partial<DraftPosition>) {
    setState((s) => ({
      ...s,
      positionen: [
        ...s.positionen,
        {
          typ: partial.typ || "freitext",
          menge: partial.menge ?? 1,
          einheit: resolveMaterialUnit(partial.einheit),
          text: partial.text ?? "",
          auftrag_id: aid,
          localId: newPositionId(),
          hero_nr: partial.hero_nr ?? null,
          hero_ean: partial.hero_ean ?? null,
          hero_unit: partial.hero_unit ?? null,
        },
      ],
    }));
  }

  function updatePosition(localId: string, patch: Partial<DraftPosition>) {
    setState((s) => ({
      ...s,
      positionen: s.positionen.map((p) => (p.localId === localId ? { ...p, ...patch } : p)),
    }));
  }

  function removePosition(localId: string) {
    setState((s) => ({ ...s, positionen: s.positionen.filter((p) => p.localId !== localId) }));
  }

  function applyAddress(addr: DeliveryAddress | null, opts?: { fillZhd?: boolean }) {
    if (!addr) {
      setState((s) => ({
        ...s,
        ...clearAddressFields(),
        lieferadresseZhd: "",
      }));
      return;
    }
    setState((s) => ({
      ...s,
      ...addressFieldsFromDelivery(addr),
      lieferadresseZhd:
        opts?.fillZhd && !s.lieferadresseZhd.trim()
          ? defaultZhd
          : s.lieferadresseZhd,
    }));
  }

  function setLieferart(next: OrderLieferart) {
    if (next === "abholung") {
      setState((s) => ({
        ...s,
        lieferart: next,
        ...clearAddressFields(),
        lieferadresseZhd: "",
      }));
      return;
    }
    setState((s) => {
      if (s.lieferadresseId) {
        return { ...s, lieferart: next };
      }
      const defaultAddr = addresses.find((a) => a.is_default) || null;
      if (defaultAddr) {
        return {
          ...s,
          lieferart: next,
          ...addressFieldsFromDelivery(defaultAddr),
          lieferadresseZhd: s.lieferadresseZhd.trim() || defaultZhd,
        };
      }
      return {
        ...s,
        lieferart: next,
        lieferadresseZhd: s.lieferadresseZhd.trim() || defaultZhd,
      };
    });
  }

  useEffect(() => {
    if (!open) {
      deliveryInitRef.current = false;
      return;
    }
    if (deliveryInitRef.current || editing) return;
    if (state.lieferart === "abholung" || state.lieferadresseId) {
      deliveryInitRef.current = true;
      return;
    }

    const defaultAddr = addresses.find((a) => a.is_default);
    if (!defaultAddr && addresses.length === 0) return;

    deliveryInitRef.current = true;
    setState((s) => {
      if (s.lieferadresseId || s.lieferart === "abholung") return s;
      if (defaultAddr) {
        return {
          ...s,
          ...addressFieldsFromDelivery(defaultAddr),
          lieferadresseZhd: s.lieferadresseZhd.trim() || defaultZhd,
        };
      }
      if (!s.lieferadresseZhd.trim() && defaultZhd) {
        return { ...s, lieferadresseZhd: defaultZhd };
      }
      return s;
    });
  }, [open, editing, addresses, state.lieferart, state.lieferadresseId, defaultZhd, setState]);

  const selectedAddr = addresses.find((a) => a.id === state.lieferadresseId) || null;
  const previewAddr = isLieferung ? composeLieferadresseString(state) : "";

  const firstHasItems =
    !!state.auftraege[0] &&
    state.positionen.some((p) => Number(p.auftrag_id) === Number(state.auftraege[0]?.id));

  const showMaContext = !!state.sourceMaId;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        dirty={wizardDirty}
        title={title}
        size={showMaContext ? "4xl" : "xl"}
        zIndexClass="z-[110]"
        panelClassName={showMaContext ? "sm:max-h-[92vh] sm:min-h-0" : undefined}
        bodyClassName={
          showMaContext
            ? "!flex !min-h-0 !flex-col !py-3 max-sm:!overflow-y-auto max-sm:material-mobile-scroll lg:!overflow-hidden"
            : undefined
        }
        footer={
          <footer className="flex w-full flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" data-tour="bestellung-entwurf" onClick={onSaveDraft}>
              Entwurf speichern
            </Button>
            {editing ? (
              <>
                <Button type="button" variant="warning" onClick={onUpdate}>
                  Bestellung aktualisieren
                </Button>
                <Button type="button" onClick={onUpdateAndMail}>
                  Aktualisieren &amp; Artikel bestellen
                </Button>
              </>
            ) : (
              <>
                {showPrintButton && onPrint ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={onPrint}
                    disabled={!hasPositions}
                  >
                    Bestellung drucken
                  </Button>
                ) : null}
                <Button type="button" data-tour="bestellung-absenden" onClick={onOrder} disabled={!hasPositions}>
                  Artikel bestellen
                </Button>
              </>
            )}
          </footer>
        }
      >
        <MaWizardSplitLayout sourceMaId={state.sourceMaId}>
        <ol className="mb-4 flex gap-2 border-b border-separator pb-3">
          {STEPS.map((st, i) => (
            <li
              key={st.key}
              className={clsx(
                "flex items-center gap-2 text-sm",
                activeStep === st.key ? "font-semibold text-accent" : "text-label-secondary",
                (st.key === "supplier" && hasSupplier) ||
                  (st.key === "auftrag" && hasAuftrag) ||
                  (st.key === "positions" && hasPositions)
                  ? "opacity-100"
                  : "",
              )}
            >
              <span
                className={clsx(
                  "flex h-6 w-6 items-center justify-center rounded-full text-xs",
                  activeStep === st.key ? "bg-accent text-label-on-accent" : "bg-fill-tertiary",
                )}
              >
                {i + 1}
              </span>
              {st.label}
            </li>
          ))}
        </ol>

        {showDraftPicker ? (
          <OrderDraftPickerPanel drafts={drafts} onLoad={onLoadDraft} onDelete={onDeleteDraft} />
        ) : null}

        <section className="mb-4 grid gap-3 lg:grid-cols-2">
          <article className="rounded-ios border border-separator p-3" data-tour="bestellung-lieferant">
            <LockedAutocomplete<HeroSupplier>
              label="Lieferant *"
              required
              placeholder="Schüco, Marx, …"
              value={supplierValue}
              minChars={0}
              loadOptions={loadOrderSupplierOptions}
              onChange={(opt) => {
                void selectSupplier(opt);
              }}
            />
            <LockedAutocomplete<HeroSupplierContact>
              label="Ansprechpartner"
              className="mt-3"
              placeholder="Name oder E-Mail …"
              value={contactValue}
              minChars={0}
              disabled={!hasSupplier}
              disabledHint="Bitte zuerst einen Lieferanten wählen."
              loadOptions={(q) => loadOrderSupplierContactOptions(state.supplier, q)}
              onChange={(opt) => {
                if (!opt) {
                  setSupplier({
                    anrede: "",
                    email: "",
                    contact_first_name: "",
                    contact_last_name: "",
                    person: "",
                  });
                  return;
                }
                const c = opt.value;
                const nextSupplier: OrderSupplierState = {
                  ...(state.supplier || { id: null, name: "" }),
                  ...supplierFieldsFromContact(c),
                };
                setSupplier(nextSupplier);
                const contactKey = (c.email || c.value || "").trim().toLowerCase();
                void applyTemplatePreferenceForContact(nextSupplier, contactKey);
              }}
            />
            <label className="mt-3 block text-xs font-medium text-label-secondary">E-Mail-Vorlage</label>
            <select
              className="eh-input mt-1 w-full"
              value={state.templateId ?? ""}
              onChange={(e) => {
                const templateId = e.target.value ? Number(e.target.value) : null;
                setState((s) => ({ ...s, templateId }));
                const sup = state.supplier;
                const supplierId = sup?.id != null ? String(sup.id) : "";
                const contactKey = (sup?.email || sup?.person || "").trim().toLowerCase();
                if (supplierId && !supplierId.startsWith("name:") && contactKey && templateId) {
                  void saveTemplatePreference(supplierId, contactKey, templateId);
                }
              }}
            >
              <option value="">— Vorlage wählen —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.is_default ? " (Standard)" : ""}
                </option>
              ))}
            </select>
          </article>

          <article className="rounded-ios border border-separator p-3">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">Lieferung / Abholung</h3>
              <SegmentedControl
                value={state.lieferart}
                onChange={setLieferart}
                options={[
                  { value: "lieferung", label: "Lieferung" },
                  { value: "abholung", label: "Abholung" },
                ]}
                name="Lieferart"
              />
            </div>
            {isLieferung ? (
              <>
                <select
                  className="eh-input w-full"
                  value={state.lieferadresseId ?? ""}
                  onChange={(e) => {
                    const id = e.target.value ? Number(e.target.value) : null;
                    const addr = addresses.find((a) => a.id === id) || null;
                    applyAddress(addr, { fillZhd: true });
                  }}
                >
                  <option value="">— Lieferadresse wählen —</option>
                  {addresses.map((a) => (
                    <option key={a.id} value={a.id}>
                      {[
                        a.is_default ? "★" : null,
                        a.abkuerzung,
                        a.firma,
                        [a.postleitzahl, a.stadt].filter(Boolean).join(" "),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </option>
                  ))}
                </select>
                <p className="mt-2 flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="secondary" onClick={() => setAddrDialog("new")}>
                    + Neue Adresse
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={!selectedAddr}
                    onClick={() => setAddrDialog("edit")}
                  >
                    Bearbeiten
                  </Button>
                </p>
                {state.lieferadresseId ? (
                  <TextInput
                    label="z.Hd."
                    className="mt-2"
                    value={state.lieferadresseZhd}
                    onChange={(e) => setState((s) => ({ ...s, lieferadresseZhd: e.target.value }))}
                  />
                ) : null}
                {previewAddr ? (
                  <pre className="mt-2 whitespace-pre-wrap rounded-md bg-fill-tertiary p-2 text-xs">
                    {previewAddr}
                  </pre>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-label-secondary">
                Es wird keine Lieferadresse in die Bestellung aufgenommen. In der E-Mail erscheint „Abholung“.
              </p>
            )}
          </article>
        </section>

        <section className="space-y-3">
          {state.auftraege.map((a) => {
            const tk =
              state.editingBestellnummerByAuftrag[a.id] ||
              state.tkPreview[a.id] ||
              "TK …";
            const positions = state.positionen.filter((p) => Number(p.auftrag_id) === Number(a.id));
            return (
              <article key={a.id} className="rounded-ios border border-separator p-3">
                <header className="mb-2 flex items-start justify-between gap-2">
                  <section>
                    <span className="rounded bg-fill-tertiary px-2 py-0.5 text-xs font-medium text-label">
                      {tk}
                    </span>
                    <h4 className="mt-1 font-semibold">{formatAuftragHeadline(a)}</h4>
                  </section>
                  {!editing ? (
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeAuftrag(a.id)}>
                      ×
                    </Button>
                  ) : null}
                </header>
                <div className="mb-2 flex flex-wrap items-end gap-2">
                  <Autocomplete<HeroArticleSuggestion>
                    className="min-w-[12rem] flex-1"
                    label="Hero-Artikel"
                    placeholder="Nr., EAN, Bezeichnung …"
                    minChars={0}
                    clearOnSelect
                    loadOptions={async (q): Promise<AutocompleteOption<HeroArticleSuggestion>[]> => {
                      if (!q.trim()) {
                        const top = await topOrderProducts(state.supplier?.id);
                        return top.map((m) => {
                          const heroNr = (m.hero_nr || "").trim();
                          const text = (m.text || "").trim();
                          const article: HeroArticleSuggestion = {
                            label: text || heroNr || "-",
                            value: heroNr || text,
                            artikel_id: null,
                            hero_nr: heroNr || null,
                            hero_base_name: text || null,
                            hero_unit: m.hero_unit || m.einheit || null,
                            einheit: m.einheit || null,
                            ean: m.hero_ean || null,
                            image_src: m.image_src || null,
                          };
                          const label = `${article.label}${m.order_count ? ` · ${m.order_count}×` : ""}`;
                          return withArtikelImage({
                            key: heroNr || text,
                            label,
                            value: article,
                          });
                        });
                      }
                      const list = await searchHeroArticles(q);
                      return list.map((m) =>
                        withArtikelImage({
                          key: m.hero_nr || m.value,
                          label: m.label,
                          value: m,
                        }),
                      );
                    }}
                    onSelect={(opt) => {
                      if (!opt) return;
                      const m = opt.value as {
                        hero_nr?: string;
                        einheit?: string;
                        label?: string;
                        text?: string;
                        ean?: string;
                        hero_base_name?: string | null;
                        hero_base_description?: string | null;
                      };
                      addPosition(a.id, {
                        typ: "hero",
                        menge: 1,
                        einheit: resolveMaterialUnit(m.einheit),
                        text: orderTextWithArticleNumber(heroArticleOrderText(m), m.hero_nr),
                        hero_nr: m.hero_nr || null,
                        hero_ean: m.ean || null,
                        hero_unit: m.einheit || null,
                      });
                    }}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="shrink-0"
                    onClick={() => addPosition(a.id, { typ: "freitext", text: "" })}
                  >
                    + Freitext
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="shrink-0"
                    onClick={() => {
                      const m = window.prompt("Material/Farbe:", "Blech");
                      if (m === null) return;
                      const d = window.prompt("Dicke mm:", "1.5");
                      if (d === null) return;
                      const l = window.prompt("Länge mm:", "1000");
                      if (l === null) return;
                      const b = window.prompt("Breite mm:", "500");
                      if (b === null) return;
                      const k = window.prompt("Kanten (optional):", "");
                      const text = `${m}, ${d} mm, ${l} x ${b} mm${k ? `, ${k}` : ""}`;
                      addPosition(a.id, { typ: "freitext", text });
                    }}
                  >
                    ▭ Blech
                  </Button>
                </div>
                <ul className="space-y-2">
                  {positions.map((p) => (
                    <li
                      key={p.localId}
                      className="grid gap-2 rounded-md border border-separator p-2 sm:grid-cols-[4.5rem_4.5rem_7.5rem_1fr_auto] sm:items-end"
                    >
                      <TextInput
                        label="Menge"
                        type="number"
                        min={0}
                        inputClassName="!w-full"
                        value={String(p.menge)}
                        onChange={(e) => updatePosition(p.localId, { menge: Number(e.target.value) || 0 })}
                      />
                      <NativeSelectField
                        label="Einheit"
                        selectSize="sm"
                        value={resolveMaterialUnit(p.einheit)}
                        onChange={(e) => updatePosition(p.localId, { einheit: e.target.value })}
                      >
                        {COMMON_MATERIAL_UNITS.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </NativeSelectField>
                      <TextInput
                        label="LOT-Nr."
                        inputClassName="!w-full"
                        title="LOT-Nummer des Artikels, damit das Werkszeugnis später zugeordnet werden kann."
                        value={p.lot_nummer || ""}
                        onChange={(e) => updatePosition(p.localId, { lot_nummer: e.target.value })}
                      />
                      <TextInput
                        label="Text"
                        inputClassName="!w-full"
                        value={p.text}
                        onChange={(e) => updatePosition(p.localId, { text: e.target.value })}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        className="self-end"
                        aria-label="Position entfernen"
                        onClick={() => removePosition(p.localId)}
                      >
                        ×
                      </Button>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}

          {Array.from({ length: state.placeholders }).map((_, i) => (
            <article
              key={`ph-${i}`}
              className={clsx(
                "rounded-ios border border-dashed border-separator p-3",
                !hasSupplier && "opacity-50 pointer-events-none",
              )}
            >
              <h4 className="font-medium">Auftrag wählen</h4>
              <p className="mb-2 text-xs text-label-tertiary">
                {hasSupplier
                  ? "Auftragsnummer, Ort oder Beschreibung suchen."
                  : "Bitte zuerst einen Lieferanten wählen."}
              </p>
              <Autocomplete<AuftragMini>
                placeholder="Auftrag suchen …"
                minChars={0}
                loadOptions={async (q) => {
                  const { searchAuftraege, getRecentAuftraege } = await import("@/lib/material");
                  const { formatAuftragLookupLabel } = await import(
                    "@/features/globalUpload/formatAuftragLookupLabel"
                  );
                  const list = q.trim() ? await searchAuftraege(q) : await getRecentAuftraege({ limit: 15 });
                  return list.map((row) => ({
                    key: String(row.id),
                    label: formatAuftragLookupLabel(row),
                    value: {
                      id: row.id,
                      jahr: row.jahr ?? new Date().getFullYear(),
                      auftragsnummer: row.auftragsnummer,
                      ort: row.ort,
                      beschreibung: row.beschreibung,
                    },
                  }));
                }}
                onSelect={(opt) => {
                  if (opt?.value) addAuftrag(opt.value);
                }}
              />
              {state.placeholders > 1 || state.auftraege.length > 0 ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="mt-2"
                  onClick={() => setState((s) => ({ ...s, placeholders: Math.max(0, s.placeholders - 1) }))}
                >
                  Platzhalter entfernen
                </Button>
              ) : null}
            </article>
          ))}

          {hasSupplier && firstHasItems ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="w-full"
              onClick={() => setState((s) => ({ ...s, placeholders: s.placeholders + 1 }))}
            >
              + Weiterer Auftrag
            </Button>
          ) : null}
        </section>
        </MaWizardSplitLayout>
      </Dialog>

      <DeliveryAddressDialog
        open={addrDialog !== null}
        mode={addrDialog === "edit" ? "edit" : "new"}
        initial={addrDialog === "edit" ? selectedAddr : null}
        onClose={() => setAddrDialog(null)}
        onSaved={(saved) => {
          void reloadAddresses().then(() => {
            if (saved) applyAddress(saved, { fillZhd: true });
          });
        }}
      />
    </>
  );
}
